import type { ReviewCommentRecord } from '@mesa/core/review';
import { CheckIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useCommentResolution } from '@/features/comments/hooks/use-comment-resolution';


interface CommentResolutionButtonProps {
  comment: ReviewCommentRecord;
  className?: string;
  iconOnly?: boolean;
}

export function CommentResolutionButton({ comment, className, iconOnly = false }: CommentResolutionButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { resolveComment, unresolveComment } = useCommentResolution();

  const isResolved = comment.resolvedById !== null;

  const handleToggleResolution = async () => {
    if (!comment.id) {
      toast.error('Cannot resolve comment: missing comment ID');
      return;
    }

    setIsLoading(true);
    try {
      if (isResolved) {
        await unresolveComment(comment.id);
      } else {
        await resolveComment(comment.id);
      }
    } catch (error) {
      console.error('Error toggling comment resolution:', error);
      toast.error(isResolved ? 'Failed to unresolve comment thread' : 'Failed to resolve comment thread');
    } finally {
      setIsLoading(false);
    }
  };

  if (iconOnly) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleToggleResolution}
        disabled={isLoading}
        className={`size-8 rounded-sm p-0 ${className}`}
      >
        {isResolved ? <XIcon className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
      </Button>
    );
  }

  return (
    <Button variant="ghost" size="sm" onClick={handleToggleResolution} disabled={isLoading} className={className}>
      {isResolved ? (
        <>
          <XIcon className="h-4 w-4 mr-1" />
          {isLoading ? 'Unresolving...' : 'Unresolve conversation'}
        </>
      ) : (
        <>
          <CheckIcon className="h-4 w-4 mr-1" />
          {isLoading ? 'Resolving...' : 'Resolve conversation'}
        </>
      )}
    </Button>
  );
}
